import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Facebook, Mail, Phone, Trash2, CheckCircle2, XCircle, RotateCcw, Copy } from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-amber-500/20 text-amber-200 border-amber-400/40" },
  completed: { label: "Completed", color: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" },
  rejected: { label: "Rejected", color: "bg-rose-500/20 text-rose-200 border-rose-400/40" },
};

export default function SuperFbDeletion() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/platform/fb-deletion-requests")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.patch(`/platform/fb-deletion-requests/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request from the log? This cannot be undone.")) return;
    try {
      await api.delete(`/platform/fb-deletion-requests/${id}`);
      toast.success("Request removed");
      load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const copyEmail = (email) => {
    try {
      navigator.clipboard.writeText(email);
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy email");
    }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => (r.status || "pending") === filter);
  const counts = {
    all: rows.length,
    pending: rows.filter((r) => (r.status || "pending") === "pending").length,
    completed: rows.filter((r) => r.status === "completed").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Facebook className="w-7 h-7 text-blue-400" /> Fb Deletion Request
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Requests submitted from the public <code className="text-slate-400">/fb-data-deletion</code> page. Meta requires you to action these within 30 days.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="border-slate-700 text-slate-200" data-testid="fbdel-refresh">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: "All" },
          { k: "pending", label: "Pending" },
          { k: "completed", label: "Completed" },
          { k: "rejected", label: "Rejected" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`fbdel-filter-${t.k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${
              filter === t.k
                ? "bg-indigo-500/20 border-indigo-400/60 text-indigo-100"
                : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            {t.label} <span className="ml-1 opacity-70">({counts[t.k]})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 p-8 text-center">
          <Facebook className="w-8 h-8 text-slate-600 mx-auto" />
          <p className="text-slate-400 mt-3">No {filter === "all" ? "" : filter} deletion requests yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const status = r.status || "pending";
            const meta = STATUS_META[status] || STATUS_META.pending;
            return (
              <Card key={r.id} data-testid="fbdel-row" className="bg-slate-900 border-slate-800 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-white text-base">{r.name}</div>
                      <Badge className={`border ${meta.color}`}>{meta.label}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-300">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-indigo-300">
                        <Mail className="w-3.5 h-3.5" /> {r.email}
                      </a>
                      <button type="button" onClick={() => copyEmail(r.email)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300">
                        <Copy className="w-3 h-3" /> copy
                      </button>
                      {r.phone && (
                        <span className="inline-flex items-center gap-1.5 text-slate-400">
                          <Phone className="w-3.5 h-3.5" /> {r.phone}
                        </span>
                      )}
                    </div>
                    {r.facebook_id && (
                      <div className="mt-1 text-xs text-slate-500">
                        <span className="text-slate-600">FB:</span> {r.facebook_id}
                      </div>
                    )}
                    {r.reason && (
                      <div className="mt-3 rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-sm text-slate-300 whitespace-pre-wrap">
                        {r.reason}
                      </div>
                    )}
                    <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-x-4">
                      <span>Ref: <span className="font-mono text-slate-400">{r.id}</span></span>
                      <span>Received: {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                      {r.completed_at && <span>Completed: {new Date(r.completed_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {status !== "completed" && (
                      <Button size="sm" onClick={() => update(r.id, "completed")} data-testid={`fbdel-complete-${r.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark completed
                      </Button>
                    )}
                    {status !== "rejected" && status !== "completed" && (
                      <Button size="sm" variant="outline" onClick={() => update(r.id, "rejected")} data-testid={`fbdel-reject-${r.id}`} className="border-slate-700 text-slate-200 hover:bg-slate-800">
                        <XCircle className="w-4 h-4 mr-1.5" /> Reject
                      </Button>
                    )}
                    {status !== "pending" && (
                      <Button size="sm" variant="outline" onClick={() => update(r.id, "pending")} data-testid={`fbdel-reopen-${r.id}`} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                        <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`fbdel-delete-${r.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                      <Trash2 className="w-4 h-4 mr-1.5" /> Remove
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
